// Client push-notification token capture (Jose 2026-10-04). Runs inside the Capacitor NATIVE customer
// app (com.slik.portal) only — no-ops on a plain browser/PWA (web push is a later phase). On login we
// ask permission and get the FCM registration token (via @capacitor-firebase/messaging, which bridges
// APNs↔FCM on iOS and FCM on Android), then store it in Firestore keyed by the token, tagged with the
// client's id AND phone. The send-trigger Cloud Function matches an appointment to tokens by clientId
// OR phone (robust across portal-booked and staff-booked appointments).
import { Capacitor } from '@capacitor/core';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

let started = false;

export async function initPush(clientId: string): Promise<void> {
  if (!clientId) return;
  if (!Capacitor.isNativePlatform()) return;   // native app only — web/PWA push comes later
  if (started) return;
  started = true;
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive !== 'granted') perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== 'granted') { started = false; return; }

    // Resolve the client's phone for a robust cross-link (staff-booked appts match by phone).
    let phone = '';
    try {
      const snap = await getDoc(doc(db, 'clients', clientId));
      phone = (snap.exists() && ((snap.data() as any).phone || '')) || '';
    } catch { /* phone optional */ }

    const save = async (token: string) => {
      if (!token) return;
      try {
        await setDoc(doc(db, 'pushTokens', token), {
          clientId, phone, token,
          platform: Capacitor.getPlatform(),   // 'ios' | 'android'
          app: 'customer',
          updatedAt: serverTimestamp(),
        }, { merge: true });
      } catch (e) { console.warn('[push] token save failed', e); }
    };

    const { token } = await FirebaseMessaging.getToken();
    await save(token);
    // Token can rotate — keep Firestore current.
    await FirebaseMessaging.addListener('tokenReceived', (event: { token: string }) => { save(event.token); });
  } catch (e) {
    started = false;
    console.warn('[push] init failed', e);
  }
}
